import { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { NavLink as RouterLink } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  FormControl,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Switch,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha, keyframes } from "@mui/material/styles";
import {
  AccountBalance,
  ChevronRight,
  EventAvailable,
  CalendarViewDay,
  FilterList,
  Fingerprint,
  FlightTakeoff,
  LocalHospital,
  OpenInNew,
  PeopleOutline,
  Print,
  Refresh,
} from "@mui/icons-material";
import { pdf } from "@react-pdf/renderer";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import FilterSidebar from "../../components/filter/FilterSidebar";
import { OfficeFilter } from "../../components/filter/OfficeFilter";
import Paging from "../../components/Layout/Paging";
import Search from "../../components/search/Search";
import CustomDialog from "../../components/modals/CustomDialog";
import { EmbassyList } from "../../components/reports/embassy/EmbassyList";
import {
  StyledTableCell,
  StyledTableCellLabel,
  StyledTableCellValue,
  StyledTableRow,
} from "../../styles/tableStyles";
import { isPrivilegedTransaction } from "../../utils/authUtils";
import { selectAuth } from "../auth/authSlice";
import { AuthUser, Role } from "../auth/types/authType";
import { RoleTypes } from "../auth/types/roleTypes";
import { Company } from "../setups/types/setupTypes";
import {
  selectPreference,
  setAppliedCountry,
  toggleThis,
} from "../preferences/preferencesSlice";
import ExportToExcel from "../process/components/ExportToExcel";
import { EmployeeStatusChip } from "../process/components/EmployeeStatusChip";
import { useEmployeeListFetch } from "../process/hooks/useEmployeeListFetch";
import {
  bulkUpdateBookingFollowUp,
  changeEmployeeStatus,
  removeEmployeeFromListById,
  selectProcesses,
  updateBookingFollowUp,
} from "../process/processSlices";
import {
  BookingFollowUpStatus,
  BookingFollowUpType,
  CompletionStatus,
  CountriesList,
  Employee,
  EmployeeArgs,
  EmployeeStatus,
  GulfCountries,
  MedicalStatus,
} from "../process/types/processTypes";
import { GET_EMPLOYEE_DASHBOARD_STATS } from "../../apollo/queries/processes";
import { apolloClient } from "../../apollo/graphql";
import { FollowUpAdvanceDialog } from "./components/FollowUpAdvanceDialog";
import { BulkFollowUpUpdateDialog } from "./components/BulkFollowUpUpdateDialog";
import { FollowUpDueIndicator } from "./components/FollowUpDueIndicator";
import { FollowUpStatusChip } from "./components/FollowUpStatusChip";
import {
  WAFID_BOOKING_URL,
  countForFollowUpType,
  effectiveFollowUpStatus,
  getFollowUpForType,
  nextFollowUpStatus,
} from "./utils/followUpHelpers";

type StatusFilter = BookingFollowUpStatus | "all";

const TYPE_TABS: {
  value: BookingFollowUpType;
  labelKey: string;
  icon: React.ElementType;
}[] = [
  {
    value: BookingFollowUpType.medical,
    labelKey: "followUps.medical",
    icon: LocalHospital,
  },
  {
    value: BookingFollowUpType.tasheer,
    labelKey: "followUps.tasheer",
    icon: Fingerprint,
  },
  {
    value: BookingFollowUpType.embassy,
    labelKey: "followUps.embassy",
    icon: AccountBalance,
  },
  {
    value: BookingFollowUpType.flight,
    labelKey: "followUps.flight",
    icon: FlightTakeoff,
  },
];

const DEFAULT_STATUS_FILTERS: StatusFilter[] = [
  "all",
  BookingFollowUpStatus.new,
  BookingFollowUpStatus.booked_with_date,
  BookingFollowUpStatus.confirmed_by_employee,
  BookingFollowUpStatus.employee_completed,
];

const EMBASSY_STATUS_FILTERS: StatusFilter[] = [
  "all",
  BookingFollowUpStatus.new,
  BookingFollowUpStatus.embassy_in_progress,
  BookingFollowUpStatus.embassy_returned,
  BookingFollowUpStatus.embassy_stamped,
];

function statusFiltersForType(type: BookingFollowUpType): StatusFilter[] {
  return type === BookingFollowUpType.embassy
    ? EMBASSY_STATUS_FILTERS
    : DEFAULT_STATUS_FILTERS;
}

const GULF_COUNTRY_FLAGS: Record<string, string> = {
  saudi_arabia: "🇸🇦",
  uae: "🇦🇪",
  kuwait: "🇰🇼",
  qatar: "🇶🇦",
  jordan: "🇯🇴",
  lebanon: "🇱🇧",
  bahrain: "🇧🇭",
  oman: "🇴🇲",
};

const panelEnter = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`;

const refreshSpin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

function canManageFollowUps(roles: Role[] | undefined) {
  const r = roles ?? [];
  return (
    isPrivilegedTransaction(r, RoleTypes.ManageFollowUps) ||
    isPrivilegedTransaction(r, RoleTypes.AddMedical) ||
    isPrivilegedTransaction(r, RoleTypes.BookEmployeeTicket)
  );
}

function isTasheerFollowUpCompleted(
  type: BookingFollowUpType,
  status: BookingFollowUpStatus,
): boolean {
  return (
    type === BookingFollowUpType.tasheer &&
    status === BookingFollowUpStatus.employee_completed
  );
}

function isEmbassyStamped(
  type: BookingFollowUpType,
  status: BookingFollowUpStatus,
): boolean {
  return (
    type === BookingFollowUpType.embassy &&
    status === BookingFollowUpStatus.embassy_stamped
  );
}

function tasheerScheduleDateKey(employee: Employee): string | null {
  const followUp = getFollowUpForType(employee, BookingFollowUpType.tasheer);
  if (!followUp?.scheduledDate) return null;
  const d = new Date(followUp.scheduledDate);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().slice(0, 10);
}

export default function FollowUps() {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const { t } = useTranslation();
  const { t: te } = useTranslation("enums");
  const { user } = useAppSelector(selectAuth);
  const {
    employeesWithCount: { employees, totalCount },
    listLoading,
  } = useAppSelector(selectProcesses);
  const {
    searchText,
    appliedCountry,
    nationality,
    addressRegion,
    brokerName,
    maritalStatus,
    religion,
    profession,
    arabic,
    amharic,
    english,
    ageStart,
    ageEnd,
    experienceYears,
    agencyId,
    agentId,
    cocCompleted: coc,
    medicalStatus: medical,
    biometricsCompleted: biometrics,
  } = useAppSelector(selectPreference);

  const [activeType, setActiveType] = useState(BookingFollowUpType.medical);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [selectAll, setSelectAll] = useState(false);
  const [debouncedSearchText, setDebouncedSearchText] = useState(
    () => searchText ?? "",
  );
  const [followUpCounts, setFollowUpCounts] = useState<
    { type: BookingFollowUpType; byStatus: { status: string; count: number }[] }[]
  >([]);
  const [selected, setSelected] = useState<Employee[]>([]);
  const [advanceEmployee, setAdvanceEmployee] = useState<Employee | null>(null);
  const [openBulkFollowUp, setOpenBulkFollowUp] = useState(false);
  const [advanceStatus, setAdvanceStatus] = useState<
    BookingFollowUpStatus | undefined
  >();
  const [submitting, setSubmitting] = useState(false);
  const [statsNonce, setStatsNonce] = useState(0);
  const [statsLoading, setStatsLoading] = useState(false);

  useEffect(() => {
    const id = window.setTimeout(
      () => setDebouncedSearchText(searchText ?? ""),
      400,
    );
    return () => window.clearTimeout(id);
  }, [searchText]);

  useEffect(() => {
    setTotal(totalCount);
  }, [totalCount]);

  const listFetchParams = useMemo((): EmployeeArgs => {
    const params: EmployeeArgs = {
      skip: currentPage * rowsPerPage,
      take: rowsPerPage,
      searchText: debouncedSearchText || undefined,
      appliedCountry: appliedCountry || undefined,
      followUpType: activeType,
      nationality,
      region: addressRegion,
      brokerName,
      maritalStatus,
      religion,
      profession,
      arabic,
      amharic,
      english,
      ageStart,
      ageEnd,
      experienceYears,
      agencyId,
      agentId,
      cocCompleted:
        coc && coc === CompletionStatus.completed
          ? true
          : coc === CompletionStatus.not_completed
            ? false
            : undefined,
      medicalStatus: medical as MedicalStatus | undefined,
      biometricsCompleted:
        biometrics && biometrics === CompletionStatus.completed
          ? true
          : biometrics === CompletionStatus.not_completed
            ? false
            : undefined,
    };
    if (statusFilter !== "all") {
      params.followUpStatus = statusFilter;
    }
    return params;
  }, [
    activeType,
    addressRegion,
    ageEnd,
    ageStart,
    agencyId,
    agentId,
    appliedCountry,
    arabic,
    amharic,
    biometrics,
    brokerName,
    coc,
    currentPage,
    debouncedSearchText,
    english,
    experienceYears,
    maritalStatus,
    medical,
    nationality,
    profession,
    religion,
    rowsPerPage,
    statusFilter,
  ]);

  const { refreshList } = useEmployeeListFetch(listFetchParams);

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const response = await apolloClient.query({
        query: GET_EMPLOYEE_DASHBOARD_STATS,
        variables: {
          searchText: debouncedSearchText || undefined,
          appliedCountry: appliedCountry || undefined,
        },
        fetchPolicy: "network-only",
      });
      setFollowUpCounts(response.data?.employeeDashboardStats?.followUpCounts ?? []);
    } catch {
      setFollowUpCounts([]);
    } finally {
      setStatsLoading(false);
    }
  }, [appliedCountry, debouncedSearchText]);

  useEffect(() => {
    void fetchStats();
  }, [fetchStats, statsNonce]);

  const handleRefresh = useCallback(() => {
    refreshList();
    setStatsNonce((n) => n + 1);
  }, [refreshList]);

  const handleDrawerOpen = () => {
    dispatch(toggleThis({ type: "Filter", newValue: true }));
  };

  const canManage = canManageFollowUps(user?.roles);

  // Embassy tracks a submission date (not an appointment), so relabel the date
  // column and skip the appointment "due" indicator for it.
  const isEmbassyType = activeType === BookingFollowUpType.embassy;
  const dateColumnLabel = isEmbassyType
    ? t("followUps.submittedDate")
    : t("followUps.scheduledDate");

  const tasheerExportEmployees = useMemo(() => {
    if (activeType !== BookingFollowUpType.tasheer || selected.length === 0) {
      return [];
    }
    const dateKeys = selected.map((e) => tasheerScheduleDateKey(e));
    if (dateKeys.some((key) => key == null)) return [];
    const uniqueDates = new Set(dateKeys);
    if (uniqueDates.size !== 1) return [];
    return selected;
  }, [activeType, selected]);

  const tasheerExportDateMismatch = useMemo(
    () =>
      activeType === BookingFollowUpType.tasheer &&
      selected.length > 0 &&
      tasheerExportEmployees.length === 0,
    [activeType, selected.length, tasheerExportEmployees.length],
  );

  const showTasheerExport =
    activeType === BookingFollowUpType.tasheer &&
    Boolean(user?.company) &&
    tasheerExportEmployees.length > 0;

  const showEmbassyListPrint =
    activeType === BookingFollowUpType.embassy && selected.length > 0;

  const printEmbassyList = async () => {
    if (!selected.length) return;
    try {
      const doc = EmbassyList({
        employees: selected,
        user: user as AuthUser,
      });
      const blob = await pdf(doc).toBlob();
      const url = URL.createObjectURL(blob);
      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      document.body.appendChild(iframe);
      iframe.src = url;
      iframe.onload = function () {
        setTimeout(function () {
          iframe.focus();
          iframe.contentWindow?.print();
          setTimeout(() => {
            document.body.removeChild(iframe);
            URL.revokeObjectURL(url);
          }, 1000);
        }, 1);
      };
    } catch (e) {
      console.error(e);
    }
  };

  const secondaryDark = theme.palette.secondary.dark;
  const secondaryContrast =
    theme.palette.secondary.contrastText ?? theme.palette.common.white;
  const pillInactiveBg =
    theme.palette.mode === "light"
      ? theme.palette.grey[100]
      : alpha(theme.palette.common.white, 0.06);
  const pillInactiveHoverBg =
    theme.palette.mode === "light"
      ? theme.palette.grey[200]
      : alpha(theme.palette.common.white, 0.1);
  const glowHue = theme.palette.secondary.main;
  const pillSelectedGlow =
    theme.palette.mode === "light"
      ? `0 0 0 2px ${alpha(secondaryContrast, 0.35)}, 0 0 18px ${alpha(glowHue, 0.5)}, 0 5px 22px ${alpha(glowHue, 0.32)}`
      : `0 0 0 2px ${alpha(glowHue, 0.45)}, 0 0 22px ${alpha(glowHue, 0.55)}, 0 6px 28px ${alpha(glowHue, 0.38)}`;
  const pillSelectedGlowHover =
    theme.palette.mode === "light"
      ? `0 0 0 2px ${alpha(secondaryContrast, 0.45)}, 0 0 24px ${alpha(glowHue, 0.62)}, 0 7px 28px ${alpha(glowHue, 0.4)}`
      : `0 0 0 2px ${alpha(glowHue, 0.55)}, 0 0 28px ${alpha(glowHue, 0.68)}, 0 8px 34px ${alpha(glowHue, 0.45)}`;

  const pillSelectedSx = {
    bgcolor: secondaryDark,
    color: secondaryContrast,
    borderWidth: 2,
    borderStyle: "solid" as const,
    borderColor: secondaryDark,
    position: "relative" as const,
    zIndex: 1,
    boxShadow: pillSelectedGlow,
    "&:hover": {
      bgcolor: secondaryDark,
      color: secondaryContrast,
      borderColor: secondaryDark,
      filter: "brightness(1.06)",
      boxShadow: pillSelectedGlowHover,
    },
  };

  const pillInactiveSx = {
    color: secondaryDark,
    borderWidth: 2,
    borderStyle: "solid" as const,
    borderColor: secondaryDark,
    bgcolor: pillInactiveBg,
    "&:hover": {
      color: secondaryDark,
      borderColor: secondaryDark,
      bgcolor: pillInactiveHoverBg,
      boxShadow: "none",
    },
  };

  const pillButtonSx = (selected: boolean) => ({
    borderRadius: 999,
    px: 1.5,
    py: 0.5,
    minWidth: 0,
    textTransform: "none" as const,
    fontWeight: 800,
    letterSpacing: 0.02,
    boxShadow: "none",
    transition: theme.transitions.create(
      ["background-color", "color", "border-color", "box-shadow"],
      { duration: theme.transitions.duration.short },
    ),
    ...(selected ? pillSelectedSx : pillInactiveSx),
  });

  const typeTabCountLabel = (type: BookingFollowUpType) => {
    if (statsLoading) return "…";
    return String(countForFollowUpType(followUpCounts, type));
  };

  const statusCountLabel = (filter: StatusFilter) => {
    if (statsLoading) return "…";
    if (filter === "all") {
      return String(countForFollowUpType(followUpCounts, activeType));
    }
    return String(countForFollowUpType(followUpCounts, activeType, filter));
  };

  const isRowSelected = (id?: string) =>
    selected.some((e) => String(e._id) === String(id));

  const toggleSelected = (emp: Employee) => {
    setSelected((prev) => {
      const exists = prev.some((e) => e._id === emp._id);
      if (exists) return prev.filter((e) => e._id !== emp._id);
      return [...prev, emp];
    });
    setSelectAll(false);
  };

  const changeSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setSelectAll(checked);
    setSelected(checked ? [...employees] : []);
  };

  const markTasheerBiometricsCompleted = async (rows: Employee[]) => {
    await Promise.all(
      rows
        .filter((e) => e._id && e.status)
        .map((e) =>
          dispatch(
            changeEmployeeStatus({
              _id: String(e._id),
              status: e.status as EmployeeStatus,
              biometricsCompleted: true,
            }),
          ),
        ),
    );
  };

  const removeRowsFromList = (rows: Employee[]) => {
    rows.forEach((e) => {
      if (!e._id) return;
      dispatch(removeEmployeeFromListById(String(e._id)));
    });
    const removedIds = new Set(rows.map((e) => String(e._id)));
    setSelected((prev) => prev.filter((e) => !removedIds.has(String(e._id))));
  };

  const handleAdvance = async (payload: {
    status: BookingFollowUpStatus;
    scheduledDate?: Date;
    remark?: string;
  }) => {
    if (!advanceEmployee?._id) return;
    const employeeSnapshot = advanceEmployee;
    setSubmitting(true);
    const result = await dispatch(
      updateBookingFollowUp({
        employeeId: advanceEmployee._id,
        type: activeType,
        status: payload.status,
        scheduledDate: payload.scheduledDate,
        remark: payload.remark,
      }),
    );
    setSubmitting(false);
    if (updateBookingFollowUp.fulfilled.match(result)) {
      setAdvanceEmployee(null);
      setAdvanceStatus(undefined);
      if (isTasheerFollowUpCompleted(activeType, payload.status)) {
        await markTasheerBiometricsCompleted([employeeSnapshot]);
        setStatusFilter(BookingFollowUpStatus.employee_completed);
      } else if (isEmbassyStamped(activeType, payload.status)) {
        setStatusFilter(BookingFollowUpStatus.embassy_stamped);
      } else if (statusFilter !== "all" && payload.status !== statusFilter) {
        removeRowsFromList([employeeSnapshot]);
      }
      handleRefresh();
    }
  };

  const handleBulkAdvance = async (status: BookingFollowUpStatus) => {
    if (selected.length === 0) return;
    const selectedSnapshot = [...selected];
    setSubmitting(true);
    const result = await dispatch(
      bulkUpdateBookingFollowUp({
        ids: selectedSnapshot.map((e) => String(e._id)),
        type: activeType,
        status,
      }),
    );
    setSubmitting(false);
    if (bulkUpdateBookingFollowUp.fulfilled.match(result)) {
      if (isTasheerFollowUpCompleted(activeType, status)) {
        await markTasheerBiometricsCompleted(selectedSnapshot);
        setStatusFilter(BookingFollowUpStatus.employee_completed);
      }
      setSelected([]);
      setSelectAll(false);
      handleRefresh();
    }
  };

  const nextBulkStatus = useMemo(() => {
    if (selected.length === 0) return null;
    // Embassy is non-linear (returned-for-fixing branch): no single "next" —
    // managers use the Group Update dialog to pick a target status.
    if (activeType === BookingFollowUpType.embassy) return null;
    const statuses = selected.map((e) =>
      effectiveFollowUpStatus(getFollowUpForType(e, activeType)),
    );
    const order = [
      BookingFollowUpStatus.new,
      BookingFollowUpStatus.booked_with_date,
      BookingFollowUpStatus.confirmed_by_employee,
      BookingFollowUpStatus.employee_completed,
    ];
    const minIdx = Math.min(...statuses.map((s) => order.indexOf(s)));
    return minIdx < order.length - 1 ? order[minIdx + 1] : null;
  }, [activeType, selected]);

  const showTableSkeleton = listLoading === "pending" && employees.length === 0;
  const tableColSpan = canManage ? 9 : 8;

  const renderCountryFilterPills = () => (
    <Stack
      direction="row"
      flexWrap="wrap"
      alignItems="center"
      justifyContent="center"
      sx={{
        gap: 0.75,
        width: "100%",
        maxWidth: "100%",
        flex: { xs: "none", md: "0 1 auto" },
        px: { md: 1 },
      }}
    >
      <Button
        size="small"
        variant="outlined"
        disableElevation
        onClick={() => {
          setCurrentPage(0);
          dispatch(setAppliedCountry(undefined));
        }}
        sx={pillButtonSx(appliedCountry === undefined)}
      >
        {t("allCountries")}
      </Button>
      {Object.keys(GulfCountries).map((key) => (
        <Button
          key={key}
          size="small"
          variant="outlined"
          disableElevation
          onClick={() => {
            setCurrentPage(0);
            dispatch(setAppliedCountry(key as CountriesList));
          }}
          sx={pillButtonSx(appliedCountry === key)}
        >
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <span style={{ fontSize: "1.1rem", lineHeight: 1 }}>
              {GULF_COUNTRY_FLAGS[key]}
            </span>
            <span>{te(key)}</span>
          </Stack>
        </Button>
      ))}
    </Stack>
  );

  const renderAdvanceButton = (row: Employee, size: "small" | "medium" = "small") => {
    if (!canManage) return null;
    const status = effectiveFollowUpStatus(getFollowUpForType(row, activeType));
    // Embassy is non-linear: open the dialog with a status picker (no preset target).
    if (activeType === BookingFollowUpType.embassy) {
      if (status === BookingFollowUpStatus.embassy_stamped) return null;
      return (
        <Button
          size={size}
          color="secondary"
          variant="contained"
          startIcon={<EventAvailable />}
          onClick={() => {
            setAdvanceEmployee(row);
            setAdvanceStatus(undefined);
          }}
        >
          {t("followUps.updateStatus")}
        </Button>
      );
    }
    const next = nextFollowUpStatus(status, activeType);
    if (!next) return null;
    return (
      <Tooltip title={te(`bookingFollowUp_${next}`)}>
        <Button
          size={size}
          color="secondary"
          variant="contained"
          startIcon={<EventAvailable />}
          onClick={() => {
            setAdvanceEmployee(row);
            setAdvanceStatus(next);
          }}
        >
          {te(`bookingFollowUp_${next}`)}
        </Button>
      </Tooltip>
    );
  };

  return (
    <>
      <Helmet>
        <title>{t("followUps.title")} | CvPlus</title>
      </Helmet>

      <Box sx={{ m: { xs: 0, md: 1 }, px: { xs: 0, md: 2 } }}>
        <Stack spacing={1.25} sx={{ width: "100%" }}>
          {/* Mobile toolbar */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            spacing={1}
            sx={{ display: { xs: "flex", md: "none" }, px: 1, pt: 1 }}
          >
            {showTasheerExport && user?.company && (
              <ExportToExcel
                selectedEmployees={tasheerExportEmployees}
                company={user.company as Company}
                user={user as AuthUser}
              />
            )}
            {showEmbassyListPrint && (
              <Tooltip title="Print Embassy List">
                <Button
                  color="warning"
                  variant="contained"
                  onClick={printEmbassyList}
                  size="large"
                >
                  <Print />
                </Button>
              </Tooltip>
            )}
            <Tooltip title={t("filter")}>
              <Button
                color="secondary"
                variant="contained"
                onClick={handleDrawerOpen}
                size="large"
              >
                <FilterList />
              </Button>
            </Tooltip>
          </Stack>

          {/* Desktop toolbar */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ display: { xs: "none", md: "flex" }, columnGap: 1 }}
          >
            <Box sx={{ flex: "1 1 0", minWidth: 0 }} />
            {renderCountryFilterPills()}
            <Stack
              direction="row"
              spacing={1}
              sx={{ flex: "1 1 0", minWidth: 0, justifyContent: "flex-end" }}
            >
              <Tooltip title={t("filter")}>
                <Button
                  color="secondary"
                  variant="contained"
                  onClick={handleDrawerOpen}
                  size="large"
                >
                  <FilterList />
                </Button>
              </Tooltip>
            </Stack>
          </Stack>

          {/* Mobile country select */}
          <Box sx={{ width: "100%", display: { xs: "block", md: "none" }, px: 1 }}>
            <FormControl fullWidth size="small" variant="outlined">
              <Select
                displayEmpty
                value={appliedCountry ?? ""}
                onChange={(e: SelectChangeEvent<string>) => {
                  const v = e.target.value;
                  setCurrentPage(0);
                  dispatch(
                    setAppliedCountry(
                      v === "" ? undefined : (v as CountriesList),
                    ),
                  );
                }}
              >
                <MenuItem value="">
                  <em>{t("allCountries")}</em>
                </MenuItem>
                {Object.keys(GulfCountries).map((key) => (
                  <MenuItem key={key} value={key}>
                    {te(key)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Search + refresh row */}
          <Box
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              px: { xs: 1, md: 0 },
            }}
          >
            <Tooltip title={t("refresh")}>
              <span>
                <Button
                  color="secondary"
                  variant="contained"
                  disableElevation
                  size="small"
                  startIcon={
                    <Refresh
                      sx={{
                        animation:
                          listLoading === "pending"
                            ? `${refreshSpin} 0.8s linear infinite`
                            : "none",
                      }}
                    />
                  }
                  onClick={handleRefresh}
                  disabled={listLoading === "pending"}
                  sx={{
                    borderRadius: 999,
                    textTransform: "none",
                    fontWeight: 700,
                    px: 2,
                    flexShrink: 0,
                  }}
                >
                  {t("refresh")}
                </Button>
              </span>
            </Tooltip>
            <OfficeFilter />
            <Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
              <Box sx={{ width: "100%", maxWidth: { xs: "100%", sm: 560 } }}>
                <Search initialValue={searchText} />
              </Box>
            </Box>
            {showTasheerExport && user?.company && (
              <ExportToExcel
                selectedEmployees={tasheerExportEmployees}
                company={user.company as Company}
                user={user as AuthUser}
              />
            )}
            {showEmbassyListPrint && (
              <Tooltip title="Print Embassy List">
                <Button
                  color="secondary"
                  variant="contained"
                  onClick={printEmbassyList}
                  size="medium"
                >
                  <Print /> Embassy List
                </Button>
              </Tooltip>
            )}
          </Box>

          {/* Type tabs — same dark bar as Employees pipeline tabs */}
          <Box
            sx={{
              mt: 0.5,
              px: 1,
              py: 0.5,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.primary.dark, 0.85),
            }}
          >
            <Tabs
              value={activeType}
              onChange={(_, v) => {
                setActiveType(v);
                setCurrentPage(0);
                setSelected([]);
                setSelectAll(false);
                setStatusFilter("all");
              }}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              textColor="secondary"
              indicatorColor="secondary"
              sx={{
                minHeight: 42,
                "& .MuiTabs-indicator": {
                  height: 3,
                  borderRadius: 3,
                  backgroundColor: theme.palette.common.white,
                },
                "& .MuiTab-root": {
                  minHeight: 42,
                  borderRadius: 1.5,
                  gap: 0.75,
                  textTransform: "none",
                  fontWeight: 700,
                  color: alpha(theme.palette.common.white, 0.72),
                },
                "& .MuiTab-root:hover": {
                  color: theme.palette.common.white,
                  backgroundColor: alpha(theme.palette.common.white, 0.12),
                },
                "& .MuiTab-root.Mui-selected": {
                  color: theme.palette.common.white,
                  backgroundColor: alpha(theme.palette.common.white, 0.22),
                  transform: "translateY(-1px)",
                },
                "& .MuiTabs-scrollButtons": {
                  color: theme.palette.common.white,
                  opacity: 1,
                },
              }}
            >
              {TYPE_TABS.map((tab, index) => {
                const TabIcon = tab.icon;
                return (
                  <Tab
                    key={tab.value}
                    value={tab.value}
                    icon={<TabIcon sx={{ fontSize: "1.125rem" }} />}
                    iconPosition="start"
                    label={
                      <Stack direction="row" alignItems="center" spacing={0.25}>
                        <span>{`${t(tab.labelKey)} (${typeTabCountLabel(tab.value)})`}</span>
                        {index < TYPE_TABS.length - 1 && (
                          <ChevronRight sx={{ fontSize: "1rem", opacity: 0.4 }} />
                        )}
                      </Stack>
                    }
                  />
                );
              })}
            </Tabs>
          </Box>

          {/* Status sub-filters — pill style like country filters */}
          <Stack
            direction="row"
            flexWrap="wrap"
            sx={{ gap: 0.75, px: { xs: 1, md: 0 }, py: 0.5 }}
          >
            {statusFiltersForType(activeType).map((filter) => {
              const label =
                filter === "all"
                  ? t("all")
                  : te(`bookingFollowUp_${filter}`);
              return (
                <Button
                  key={filter}
                  size="small"
                  variant="outlined"
                  disableElevation
                  onClick={() => {
                    setStatusFilter(filter);
                    setCurrentPage(0);
                  }}
                  sx={pillButtonSx(statusFilter === filter)}
                >
                  {`${label} (${statusCountLabel(filter)})`}
                </Button>
              );
            })}
          </Stack>

          {selected.length > 0 && canManage && (
            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              sx={{ px: { xs: 1, md: 0 } }}
            >
              <Typography variant="body2" sx={{ alignSelf: "center" }}>
                {t("followUps.selected", { count: selected.length })}
              </Typography>
              {tasheerExportDateMismatch && (
                <Typography
                  variant="caption"
                  color="warning.main"
                  sx={{ alignSelf: "center" }}
                >
                  {t("followUps.exportSameDateOnly")}
                </Typography>
              )}
              {nextBulkStatus &&
                nextBulkStatus !== BookingFollowUpStatus.booked_with_date && (
                  <Button
                    size="small"
                    color="secondary"
                    variant="contained"
                    disabled={submitting}
                    onClick={() => void handleBulkAdvance(nextBulkStatus)}
                  >
                    {t("followUps.bulkAdvance", {
                      status: te(`bookingFollowUp_${nextBulkStatus}`),
                    })}
                  </Button>
                )}
              <Button
                size="small"
                color="secondary"
                variant="contained"
                disabled={submitting}
                startIcon={<CalendarViewDay />}
                onClick={() => setOpenBulkFollowUp(true)}
              >
                {t("followUps.bulkUpdate")}
              </Button>
              {activeType === BookingFollowUpType.medical && (
                <Button
                  size="small"
                  color="secondary"
                  variant="outlined"
                  startIcon={<OpenInNew />}
                  href={WAFID_BOOKING_URL}
                  target="_blank"
                >
                  Wafid
                </Button>
              )}
            </Stack>
          )}
        </Stack>

        <Stack
          direction="row"
          spacing={1}
          alignItems="flex-start"
          sx={{ width: "100%", mt: 1 }}
        >
          <Box
            key={`${activeType}-${statusFilter}`}
            sx={{
              flex: 1,
              minWidth: 0,
              animation: `${panelEnter} ${theme.transitions.duration.standard}ms ${theme.transitions.easing.easeOut} both`,
            }}
          >
            {/* Mobile cards */}
            <Box sx={{ display: { xs: "block", md: "none" }, px: 1 }}>
              {showTableSkeleton ? (
                <LinearProgress color="secondary" sx={{ mb: 1 }} />
              ) : employees.length === 0 ? (
                <Stack alignItems="center" spacing={1.5} sx={{ py: 6, opacity: 0.55 }}>
                  <PeopleOutline sx={{ fontSize: 52, color: "text.disabled" }} />
                  <Typography variant="body1" color="text.secondary" fontWeight={500}>
                    {t("no_data_found")}
                  </Typography>
                </Stack>
              ) : (
                employees.map((row) => {
                  const followUp = getFollowUpForType(row, activeType);
                  const status = effectiveFollowUpStatus(followUp);
                  return (
                    <TableContainer
                      component={Paper}
                      key={row._id}
                      sx={{
                        backgroundColor: "white",
                        borderRadius: "4px",
                        px: 1.5,
                        pt: 1,
                        mt: 1,
                      }}
                    >
                      <Table size="medium">
                        <TableBody>
                          {canManage && (
                            <StyledTableRow>
                              <StyledTableCellLabel>
                                <Tooltip title="Select">
                                  <span>{t("actions")}</span>
                                </Tooltip>
                              </StyledTableCellLabel>
                              <StyledTableCellValue>
                                <Switch
                                  checked={isRowSelected(row._id)}
                                  onChange={() => toggleSelected(row)}
                                  color="secondary"
                                  size="small"
                                />
                              </StyledTableCellValue>
                            </StyledTableRow>
                          )}
                          <StyledTableRow>
                            <StyledTableCellLabel>{t("name")}</StyledTableCellLabel>
                            <StyledTableCellValue>
                              {row.givenName} {row.surName}
                            </StyledTableCellValue>
                          </StyledTableRow>
                          <StyledTableRow>
                            <StyledTableCellLabel>{t("status")}</StyledTableCellLabel>
                            <StyledTableCellValue>
                              <EmployeeStatusChip status={row.status as EmployeeStatus} />
                            </StyledTableCellValue>
                          </StyledTableRow>
                          <StyledTableRow>
                            <StyledTableCellLabel>{t("followUps.status")}</StyledTableCellLabel>
                            <StyledTableCellValue>
                              <FollowUpStatusChip status={status} />
                            </StyledTableCellValue>
                          </StyledTableRow>
                          <StyledTableRow>
                            <StyledTableCellLabel>
                              {dateColumnLabel}
                            </StyledTableCellLabel>
                            <StyledTableCellValue>
                              <Stack spacing={0.25}>
                                {followUp?.scheduledDate
                                  ? new Date(followUp.scheduledDate).toLocaleDateString()
                                  : "—"}
                                {!isEmbassyType && (
                                  <FollowUpDueIndicator scheduledDate={followUp?.scheduledDate} />
                                )}
                              </Stack>
                            </StyledTableCellValue>
                          </StyledTableRow>
                          <StyledTableRow>
                            <StyledTableCell />
                            <StyledTableCellValue>
                              {renderAdvanceButton(row, "medium")}
                            </StyledTableCellValue>
                          </StyledTableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  );
                })
              )}
            </Box>

            {/* Desktop table */}
            <TableContainer
              component={Paper}
              sx={{
                mt: 1,
                display: { xs: "none", md: "block" },
                position: "relative",
              }}
            >
              {listLoading === "pending" && (
                <LinearProgress
                  color="secondary"
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 2,
                    height: 3,
                    borderRadius: "4px 4px 0 0",
                  }}
                />
              )}
              <Table size="medium" stickyHeader>
                <TableHead>
                  <StyledTableRow>
                    {canManage && (
                      <StyledTableCell width="3%">
                        <Tooltip title="Select All">
                          <Switch
                            checked={selectAll}
                            onChange={changeSelectAll}
                            color="secondary"
                            size="small"
                          />
                        </Tooltip>
                      </StyledTableCell>
                    )}
                    <StyledTableCell width="2%" sx={{ textTransform: "uppercase" }}>
                      {t("sno")}
                    </StyledTableCell>
                    <StyledTableCell width="5%" sx={{ textTransform: "uppercase" }}>
                      {t("photo")}
                    </StyledTableCell>
                    <StyledTableCell width="18%" sx={{ textTransform: "uppercase" }}>
                      {t("name")}
                    </StyledTableCell>
                    <StyledTableCell width="12%" sx={{ textTransform: "uppercase" }}>
                      {t("partner")}
                    </StyledTableCell>
                    <StyledTableCell sx={{ textTransform: "uppercase" }}>
                      {t("status")}
                    </StyledTableCell>
                    <StyledTableCell sx={{ textTransform: "uppercase" }}>
                      {t("followUps.status")}
                    </StyledTableCell>
                    <StyledTableCell sx={{ textTransform: "uppercase" }}>
                      {dateColumnLabel}
                    </StyledTableCell>
                    <StyledTableCell sx={{ textTransform: "uppercase" }}>
                      {t("actions")}
                    </StyledTableCell>
                  </StyledTableRow>
                </TableHead>
                <TableBody>
                  {showTableSkeleton ? (
                    <StyledTableRow>
                      <StyledTableCell colSpan={tableColSpan} align="center" sx={{ py: 6 }}>
                        <LinearProgress color="secondary" sx={{ maxWidth: 240, mx: "auto" }} />
                      </StyledTableCell>
                    </StyledTableRow>
                  ) : employees.length === 0 ? (
                    <StyledTableRow>
                      <StyledTableCell colSpan={tableColSpan} align="center" sx={{ py: 6 }}>
                        <Stack alignItems="center" spacing={1.5} sx={{ opacity: 0.55 }}>
                          <PeopleOutline sx={{ fontSize: 52, color: "text.disabled" }} />
                          <Typography variant="body1" color="text.secondary" fontWeight={500}>
                            {t("no_data_found")}
                          </Typography>
                        </Stack>
                      </StyledTableCell>
                    </StyledTableRow>
                  ) : (
                    employees.map((row, index) => {
                      const followUp = getFollowUpForType(row, activeType);
                      const status = effectiveFollowUpStatus(followUp);

                      return (
                        <StyledTableRow
                          key={row._id}
                          sx={{
                            opacity:
                              status === BookingFollowUpStatus.employee_completed
                                ? 0.65
                                : 1,
                            borderLeft:
                              status === BookingFollowUpStatus.confirmed_by_employee
                                ? `4px solid ${theme.palette.success.main}`
                                : undefined,
                          }}
                        >
                          {canManage && (
                            <StyledTableCell>
                              <Switch
                                checked={isRowSelected(row._id)}
                                onChange={() => toggleSelected(row)}
                                color="secondary"
                                size="small"
                              />
                            </StyledTableCell>
                          )}
                          <StyledTableCell>
                            <Typography color="primary" variant="h6">
                              {(currentPage * rowsPerPage + index + 1).toLocaleString()}
                            </Typography>
                          </StyledTableCell>
                          <StyledTableCell>
                            <Avatar
                              component={RouterLink}
                              alt="avatar"
                              src={row.facePhoto}
                              sx={{ cursor: "pointer", height: 40, width: 40 }}
                              to={`/app/employee-detail/${row._id}`}
                            />
                          </StyledTableCell>
                          <StyledTableCell
                            sx={{ color: "primary.main", fontWeight: 700 }}
                          >
                            <Stack spacing={0.25}>
                              <Button
                                component={RouterLink}
                                to={`/app/employee-detail/${row._id}`}
                                size="small"
                                sx={{ p: 0, minWidth: 0, fontWeight: 700, justifyContent: "flex-start" }}
                              >
                                {row.givenName} {row.surName}
                              </Button>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "text.secondary",
                                  fontSize: "0.8rem",
                                  fontWeight: 500,
                                }}
                              >
                                {row.passportNumber}
                              </Typography>
                            </Stack>
                          </StyledTableCell>
                          <StyledTableCell
                            sx={{ color: "secondary.main", fontWeight: 700 }}
                          >
                            {row.partner?.displayName?.split(" ")[0] ?? "—"}
                          </StyledTableCell>
                          <StyledTableCell>
                            <EmployeeStatusChip status={row.status as EmployeeStatus} />
                          </StyledTableCell>
                          <StyledTableCell>
                            <FollowUpStatusChip status={status} />
                          </StyledTableCell>
                          <StyledTableCell sx={{ color: "primary.main", fontWeight: 700 }}>
                            <Stack spacing={0.25}>
                              {followUp?.scheduledDate
                                ? new Date(followUp.scheduledDate).toLocaleDateString()
                                : "—"}
                              {!isEmbassyType && (
                                <FollowUpDueIndicator scheduledDate={followUp?.scheduledDate} />
                              )}
                            </Stack>
                          </StyledTableCell>
                          <StyledTableCell>
                            {renderAdvanceButton(row)}
                          </StyledTableCell>
                        </StyledTableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <FilterSidebar listType="employees" />
        </Stack>

        {employees.length > 0 && (
          <Stack spacing={1} alignItems="center" sx={{ mt: 2 }}>
            <Typography
              variant="h6"
              color="white"
              sx={{
                backgroundColor: "primary.main",
                p: 1,
                borderRadius: "4px",
              }}
            >
              {t("total")}: {total}
            </Typography>
            <Paging
              total={total}
              rowsPerPage={rowsPerPage}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              setRowsPerPage={setRowsPerPage}
            />
          </Stack>
        )}
      </Box>

      {advanceEmployee && (
        <CustomDialog
          title={t("followUps.advanceTitle", {
            name: `${advanceEmployee.givenName} ${advanceEmployee.surName}`,
          })}
          isOpen={Boolean(advanceEmployee)}
          handleDialogClose={() => {
            setAdvanceEmployee(null);
            setAdvanceStatus(undefined);
          }}
          maxWidth="sm"
          fullWidth
        >
          <Box sx={{ p: 2 }}>
            <FollowUpAdvanceDialog
              employee={advanceEmployee}
              type={activeType}
              targetStatus={advanceStatus}
              submitting={submitting}
              onSubmit={handleAdvance}
              onClose={() => {
                setAdvanceEmployee(null);
                setAdvanceStatus(undefined);
              }}
            />
          </Box>
        </CustomDialog>
      )}

      {openBulkFollowUp && (
        <BulkFollowUpUpdateDialog
          selectedEmployees={selected}
          followUpType={activeType}
          onClose={async (saved, status) => {
            setOpenBulkFollowUp(false);
            if (saved) {
              const selectedSnapshot = [...selected];
              if (status && isTasheerFollowUpCompleted(activeType, status)) {
                await markTasheerBiometricsCompleted(selectedSnapshot);
                setStatusFilter(BookingFollowUpStatus.employee_completed);
              } else if (status && isEmbassyStamped(activeType, status)) {
                setStatusFilter(BookingFollowUpStatus.embassy_stamped);
              } else if (
                statusFilter !== "all" &&
                status &&
                status !== statusFilter
              ) {
                removeRowsFromList(selectedSnapshot);
              }
              setSelected([]);
              setSelectAll(false);
              handleRefresh();
            }
          }}
        />
      )}
    </>
  );
}
